import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { ContentCopy, CheckCircleOutline } from "@mui/icons-material";
import DynamicThumbnailCard from "../../components/thumbnail/ThumbnailCard";
import type { IStreamDetails } from "../../../../types/streams";

interface StreamOverviewCardProps {
  stream: IStreamDetails;
  streamId?: number;
  refreshTrigger?: number;
  onReadyDownload?: (fn: () => void) => void;
}

function parseStreamDatetime(date: string, time: string): Date | null {
  if (!date || !time) return null;
  try {
    const parts = time.split(":");
    const h = Number(parts[0]);
    const m = Number(parts[1] ?? 0);
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    d.setHours(h, m, 0, 0);
    return d;
  } catch {
    return null;
  }
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return "00:00:00";
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (days > 0) return `${pad(days)}:${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

const CopyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body1" fontWeight={600} sx={{ mb: 0.75 }}>
        {label}
      </Typography>
      <OutlinedInput
        value={value}
        readOnly
        size="small"
        fullWidth
        placeholder="Not set"
        sx={{
          fontSize: "0.95rem",
          borderRadius: "8px",
          bgcolor: "background.paper",
          "& .MuiOutlinedInput-input": { py: 1, color: "text.secondary" },
        }}
        endAdornment={
          <InputAdornment position="end">
            <Tooltip title={copied ? "Copied!" : "Copy"}>
              <span>
                <IconButton size="small" onClick={handleCopy} disabled={!value}>
                  {copied ? (
                    <CheckCircleOutline
                      sx={{ fontSize: "1rem", color: "success.main" }}
                    />
                  ) : (
                    <ContentCopy sx={{ fontSize: "1rem" }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        }
      />
    </Box>
  );
};

const Field: React.FC<{ label: string; value?: string | number | null }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: "10px",
        backgroundColor: "#f8f9fb",
        height: "100%",
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 0.25,
          fontSize: "0.78rem",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        fontWeight={600}
        sx={{ color: "text.primary" }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const StreamOverviewCard: React.FC<StreamOverviewCardProps> = ({
  stream,
  streamId,
  refreshTrigger = 0,
  onReadyDownload,
}) => {
  const [msLeft, setMsLeft] = useState(0);

  useEffect(() => {
    const target = parseStreamDatetime(stream.date, stream.start);
    if (!target) return;
    const tick = () => setMsLeft(Math.max(0, target.getTime() - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [stream.date, stream.start]);

  const ringProgress = Math.min(
    100,
    Math.max(0, 100 - (msLeft / (24 * 3600 * 1000)) * 100),
  );

  const serviceType = stream.booking?.service ?? "";
  const isFuneral =
    serviceType.toLowerCase() === "funeral" ||
    serviceType.toLowerCase() === "funeralhome";

  const streamEnded = msLeft === 0;

  const buildFields = (): Array<{
    label: string;
    value?: string | number | null;
  }> => {
    if (isFuneral) {
      const d = stream.booking.deceased?.[0];
      return [
        { label: "Other Name", value: d?.second_name },
        { label: "Nickname", value: d?.nickname },
        { label: "Priest", value: stream.service?.preacher_name },
        { label: "Eulogist", value: stream.service?.eulogist_name },
        { label: "MC", value: stream.service?.mc_name },
      ];
    }

    if (serviceType === "Church") {
      const svc = stream.booking?.services?.[0];
      return [
        { label: "Name of Church", value: svc?.church_name },
        {
          label: "Leader Preacher",
          value:
            [svc?.leader_preacher_name, svc?.leader_preacher_surname]
              .filter(Boolean)
              .join(" ") || null,
        },
        {
          label: "Preacher",
          value:
            [svc?.preacher_name, svc?.preacher_surname]
              .filter(Boolean)
              .join(" ") || null,
        },
        { label: "Church Phone", value: stream.booking?.phone },
        { label: "Church Email", value: stream.booking?.email },
        { label: "Address Two", value: svc?.venue?.address_two },
        { label: "Suburb", value: svc?.venue?.suburb },
      ];
    }

    if (serviceType === "Conference") {
      const evt = stream.event_details;
      return [
        { label: "Expected Audience", value: evt?.expected_audience_size },
        { label: "Streaming Platform", value: evt?.streaming_platform },
        { label: "Address Two", value: evt?.venue?.address_two },
        { label: "Session to Stream", value: evt?.session_to_stream },
        { label: "Retention Duration", value: evt?.retention_duration },
      ];
    }

    if (serviceType === "Other") {
      const evt = stream.event_details;
      return [
        { label: "Host", value: evt?.hosts },
        { label: "Host Surname", value: stream.booking?.host_surname },
        { label: "Guest", value: evt?.guests },
        { label: "Guest Surname", value: stream.booking?.guest_surname },
      ];
    }

    return [];
  };

  const fields = buildFields().filter((f) => !!f.value);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Thumbnail + countdown */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <DynamicThumbnailCard
            streamId={streamId}
            streamData={stream}
            refreshTrigger={refreshTrigger}
            height={220}
            width={400}
            onReadyDownload={onReadyDownload ?? (() => {})}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 160,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress
              variant="determinate"
              value={100}
              size={160}
              thickness={3}
              sx={{ color: "#e5e7eb", position: "absolute" }}
            />
            <CircularProgress
              variant="determinate"
              value={ringProgress}
              size={160}
              thickness={3}
              sx={{ color: "primary.main" }}
            />
            <Box sx={{ position: "absolute", textAlign: "center", px: 1 }}>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: "0.05em",
                  color: streamEnded ? "text.secondary" : "text.primary",
                }}
              >
                {streamEnded ? "Stream\nEnded" : formatCountdown(msLeft)}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {streamEnded ? "" : "Time until stream"}
          </Typography>
        </Box>
      </Box>

      {/* Stream Information */}
      {(fields.length > 0 ||
        stream.youtube_stream_link ||
        stream.website_stream_link) && (
        <Paper sx={{ borderRadius: "12px", overflow: "hidden", boxShadow: 0 }}>
          <Box sx={{ px: 2, py: 1.25, bgcolor: "#f8f9fb" }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Stream Information
            </Typography>
          </Box>

          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Service-type fields grid */}
            {fields.length > 0 && (
              <Grid container spacing={1.5}>
                {fields.map((f) => (
                  <Grid key={f.label} size={{ xs: 6, sm: 4 }}>
                    <Field label={f.label} value={f.value} />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Stream links */}
            {(stream.youtube_stream_link || stream.website_stream_link) && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CopyField
                    label="Youtube Live Link"
                    value={stream.youtube_stream_link || ""}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CopyField
                    label="Website Live Link"
                    value={stream.website_stream_link || ""}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default StreamOverviewCard;
